import {
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { fuseAnimations } from '@fuse/animations';
import { select, Store } from '@ngrx/store';

import { AmplifyService } from 'aws-amplify-angular';
import { Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  takeUntil,
  mergeMap,
  tap,
} from 'rxjs/operators';
import {
  PayrollSelectors,
  PayrollUISelectors,
  PayrollExtraSelectors,
  PayrollSyntheseSelectors,
  PayrollAnalyticsSelectors,
  PayrollRuleSelectors,
  PayrollAna2Selectors,
} from './selectors';
import { CompaniesUISelectors } from '../company/selectors';
import { AppState } from 'app/reducers';
import { PayrollEntry, PayrollEntryByName } from 'app/api/payroll.api.service';
import { PayrollUIActions } from './actions';
import { PayrollExtraEntry } from 'app/api/payroll.extra.api.service';
import * as moment from 'moment';
import { PayrollSelectFilter, PayrollSyntheseEntry } from './payroll.types';
import { PayrollRuleEntry } from 'app/api/payroll.rule.api.service';

@Component({
  selector: 'payroll',
  templateUrl: './payroll.component.html',
  styleUrls: ['./payroll.component.scss'],
  animations: fuseAnimations,
  encapsulation: ViewEncapsulation.None,
})
export class PayrollComponent implements OnInit, OnDestroy, AfterViewInit {
  public gridApi;

  keyUp = new Subject<KeyboardEvent>();

  search$: Observable<string>;
  payrolls$: Observable<PayrollEntry[]>;
  payrollsUniqName$: Observable<
    {
      name: string;
      items: { timeline: string; fullRawCharges: number }[];
      totalFlux: { [year: string]: number };
    }[]
  >;
  payrollsExtra$: Observable<PayrollExtraEntry[]>;
  payrollsRule$: Observable<PayrollRuleEntry[]>;
  payrollsSynthese$: Observable<
    {
      name: string;
      choice: string;
      items: PayrollSyntheseEntry[];
    }[]
  >;
  payrollsSyntheseAna2$: Observable<
    {
      name: string;
      choice: string;
      items: PayrollSyntheseEntry[];
    }[]
  >;
  ana2PayrollRows$: Observable<
    {
      name: string;
      choice: string;
      items: any[];
    }[]
  >;

  ana2PayrollWithoutNA$: Observable<
    {
      name: string;
      choice: string;
      items: any[];
    }[]
  >;

  extraSelected: PayrollExtraEntry = {} as PayrollExtraEntry;
  companyId: string;
  companyId$: Observable<string>;
  loading$: Observable<boolean>;
  selectedTab$: Observable<number>;
  selectedYears$: Observable<string[]>;
  selectedFilter$: Observable<{
    selectedFilter: PayrollSelectFilter[];
    selectedFilterKeys: string[];
  }>;

  // Protected
  protected logger: any;

  // Private
  private _unsubscribeAll: Subject<any>;

  constructor(
    protected amplifyService: AmplifyService,
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    protected store: Store<AppState>
  ) {
    this.logger = this.amplifyService.logger('PayrollComponent');
    this._unsubscribeAll = new Subject();

    this.selectedTab$ = store.pipe(
      select(PayrollUISelectors.selectSelectedTab)
    );
  }

  ngOnInit(): void {
    this.loading$ = this.store.pipe(
      select(PayrollSelectors.selectPayrollLoading)
    );

    this.search$ = this.keyUp.pipe(
      takeUntil(this._unsubscribeAll),
      map((event: any) => event.target.value || ''),
      debounceTime(250),
      distinctUntilChanged()
    );

    this.companyId$ = this.store.pipe(
      takeUntil(this._unsubscribeAll),
      select(CompaniesUISelectors.selectCurrentCompanyId)
    );

    this.companyId$.subscribe((companyId) => {
      window.history.pushState({}, '', `/payroll/${companyId}`);
      this.companyId = companyId;
      this.payrollsExtra$ = this.store.pipe(
        takeUntil(this._unsubscribeAll),
        select(PayrollExtraSelectors.selectPayrollExtraByCompany(this.companyId))
      );
    });


    this.payrolls$ = this.store.pipe(
      takeUntil(this._unsubscribeAll),
      select(PayrollSelectors.selectPayrollCube)
    );

    this.selectedFilter$ = this.store.pipe(
      takeUntil(this._unsubscribeAll),
      select(PayrollUISelectors.selectSelectdFilter)
    );

    this.payrolls$.subscribe(() => {
      this.payrollsRule$ = this.store.pipe(
        takeUntil(this._unsubscribeAll),
        select(PayrollRuleSelectors.selectPayrollRuleByAxis('ana2'))
      );
    });

    this.payrollsUniqName$ = this.store.pipe(
      takeUntil(this._unsubscribeAll),
      select(PayrollSelectors.selectPayrollByNameUniq())
    );

    this.ana2PayrollRows$ = this.payrollsUniqName$.pipe(
      takeUntil(this._unsubscribeAll),
      mergeMap((payrollsUniqName: PayrollEntryByName[]) => {
        return this.payrollsRule$.pipe(
          mergeMap((rules: PayrollRuleEntry[]) => {
            return this.store.select(
              PayrollAnalyticsSelectors.selectPayrollAna2View(
                payrollsUniqName,
                rules
              )
            );
          })
        );
      })
    );

    // Get Year
    this.store
      .pipe(
        takeUntil(this._unsubscribeAll),
        select(CompaniesUISelectors.selectCurrentPeriod)
      )

      .subscribe((periods) => {
        const selectedYears = [];
        if (periods) {
          if (periods.from) {
            selectedYears.push(moment(periods.from.startDate).format('YYYY'));
          }
          if (periods.to) {
            selectedYears.push(moment(periods.to.startDate).format('YYYY'));
          }

          this.store.dispatch(
            PayrollUIActions.payrollSetSelectedYears({ selectedYears })
          );
        }
      });

    this.selectedYears$ = this.store.pipe(
      takeUntil(this._unsubscribeAll),
      select(PayrollUISelectors.selectSelectedYears)
    );

    this.ana2PayrollRows$.subscribe((entries) => {
      this.payrollsSynthese$ = this.store.pipe(
        takeUntil(this._unsubscribeAll),
        select(PayrollUISelectors.selectSelectdFilterSynthese),
        mergeMap((selectedFilter) => {
          return this.store
            .select(PayrollUISelectors.selectViewTypeSynthese)
            .pipe(
              mergeMap((viewType) =>
                this.store.select(
                  PayrollSyntheseSelectors.selectPayrollSyntheseBy(
                    viewType,
                    selectedFilter,
                    entries
                  )
                )
              )
            );
        })
      );
    });

    this.extraSelected = {} as PayrollExtraEntry;
  }
  // tslint:disable-next-line: typedef
  onFilterTable({ keys, states }) {
    // console.log('Filter payroll with keys::', keys);
    this.payrollsUniqName$ = this.store.pipe(
      takeUntil(this._unsubscribeAll),
      select(PayrollSelectors.selectPayrollByNameUniq(keys))
    );

    this.store.dispatch(
      PayrollUIActions.payrollSetSelectedFilter({
        selectedFilter: states,
        selectedFilterKeys: keys,
      })
    );
  }

  onSyntheseViewChanged({ viewType, axeSelected, entries }): void {
    this.payrollsSynthese$ = this.store.pipe(
      takeUntil(this._unsubscribeAll),
      select(
        PayrollSyntheseSelectors.selectPayrollSyntheseBy(
          viewType,
          axeSelected,
          entries
        )
      )
    );
  }

  onSelectTab(selectedTab: number): void {
    this.store.dispatch(
      PayrollUIActions.payrollSetSelectedTab({ selectedTab })
    );
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }
}
